import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import TvIcon from '@mui/icons-material/Tv'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SaveIcon from '@mui/icons-material/Save'
import StarIcon from '@mui/icons-material/Star'
import { Link } from 'react-router-dom'
import API from '../../../services/API'
import ScreenEditor from './ScreenEditor'
import DevicesPanel from './DevicesPanel'

// Wallboard management: build boards (screens + widgets) and manage the
// authorised displays. Site admins only (the server enforces AuthAdmin; the
// nav entry is also admin-gated).
const WallboardManager = () => {
  const [boards, setBoards] = useState(null)
  const [kpiSlugs, setKpiSlugs] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  // The draft being edited (deep copy of the selected board)
  const [draft, setDraft] = useState(null)
  const [dirty, setDirty] = useState(false)
  const [tab, setTab] = useState('layout')
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [newBoardName, setNewBoardName] = useState('')
  const [menuAnchor, setMenuAnchor] = useState(null)

  const loadBoards = useCallback(async (keepSelection = true) => {
    const res = await API.get('/wallboard/config')
    if (res.status !== 200) {
      setToast({ severity: 'error', message: 'Could not load wallboards' })
      setBoards([])
      return []
    }
    setBoards(res.data.boards)
    setKpiSlugs(res.data.kpiSlugs ?? [])
    if (!keepSelection) setSelectedId(null)
    return res.data.boards
  }, [])

  useEffect(() => {
    loadBoards()
  }, [loadBoards])

  // Select the first board once loaded (or after create/delete)
  useEffect(() => {
    if (!boards) return
    if (!selectedId || !boards.some((board) => board._id === selectedId)) {
      setSelectedId(boards[0]?._id ?? null)
    }
  }, [boards, selectedId])

  const selected = useMemo(
    () => boards?.find((board) => board._id === selectedId) ?? null,
    [boards, selectedId],
  )

  // Reset the draft whenever a different board is selected or reloaded
  useEffect(() => {
    setDraft(selected ? JSON.parse(JSON.stringify(selected)) : null)
    setDirty(false)
  }, [selected])

  const updateDraft = (updater) => {
    setDraft((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater
      return next
    })
    setDirty(true)
  }

  const save = async () => {
    if (!draft) return
    setSaving(true)
    const res = await API.put(`/wallboard/config/${draft._id}`, draft)
    setSaving(false)
    if (res.status === 200) {
      setToast({ severity: 'success', message: 'Wallboard saved — displays update within a minute' })
      await loadBoards()
    } else {
      setToast({
        severity: 'error',
        message: res.data?.error ?? 'Could not save the wallboard',
      })
    }
  }

  const createBoard = async () => {
    const res = await API.post('/wallboard/config', {
      name: newBoardName.trim() || 'Station wallboard',
      screens: [
        { name: 'Overview', dwellSeconds: 30, widgets: [] },
      ],
    })
    if (res.status === 200) {
      setCreateOpen(false)
      setNewBoardName('')
      const list = await loadBoards()
      setSelectedId(res.data._id ?? list.at(-1)?._id)
    } else {
      setToast({
        severity: 'error',
        message: res.data?.error ?? 'Could not create the wallboard',
      })
    }
  }

  const deleteBoard = async () => {
    setMenuAnchor(null)
    if (
      !window.confirm(
        `Delete "${selected?.name}"? Displays bound to it fall back to the default wallboard.`,
      )
    )
      return
    const res = await API.delete(`/wallboard/config/${selectedId}`)
    if (res.status === 200) {
      setSelectedId(null)
      await loadBoards(false)
    } else {
      setToast({ severity: 'error', message: 'Could not delete the wallboard' })
    }
  }

  const makeDefault = async () => {
    setMenuAnchor(null)
    const res = await API.put(`/wallboard/config/${selectedId}`, {
      ...(draft ?? selected),
      isDefault: true,
    })
    if (res.status === 200) await loadBoards()
  }

  if (!boards) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1700, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 1 }}>
        <TvIcon color="primary" sx={{ fontSize: 32 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Wallboards
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Build TV displays of live stats, and manage which screens are
            authorised to show them.
          </Typography>
        </Box>
        <Button
          component={Link}
          to="/wallboard"
          startIcon={<OpenInNewIcon />}
          sx={{ textTransform: 'none' }}
        >
          Open wallboard
        </Button>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          New wallboard
        </Button>
      </Stack>

      {/* Board picker */}
      <Stack direction="row" gap={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
        {boards.map((board) => (
          <Chip
            key={board._id}
            label={board.name}
            icon={board.isDefault ? <StarIcon /> : undefined}
            color={board._id === selectedId ? 'primary' : 'default'}
            onClick={() => setSelectedId(board._id)}
          />
        ))}
        {boards.length === 0 && (
          <Alert severity="info" sx={{ width: '100%' }}>
            No wallboards yet — create one to get started. The first wallboard
            becomes the default that new displays show.
          </Alert>
        )}
      </Stack>

      {selected && draft && (
        <>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <TextField
              label="Wallboard name"
              size="small"
              value={draft.name}
              onChange={(event) =>
                updateDraft((current) => ({ ...current, name: event.target.value }))
              }
              inputProps={{ maxLength: 60 }}
            />
            <TextField
              select
              size="small"
              label="Theme"
              value={draft.settings?.theme ?? 'dark'}
              onChange={(event) =>
                updateDraft((current) => ({
                  ...current,
                  settings: { ...current.settings, theme: event.target.value },
                }))
              }
              sx={{ width: 100 }}
            >
              <MenuItem value="dark">Dark</MenuItem>
              <MenuItem value="light">Light</MenuItem>
            </TextField>
            {selected.isDefault && <Chip size="small" icon={<StarIcon />} label="Default" />}
            <Box sx={{ flex: 1 }} />
            <Tabs value={tab} onChange={(event, value) => setTab(value)}>
              <Tab value="layout" label="Layout" />
              <Tab value="devices" label="Devices" />
            </Tabs>
            <Tooltip title="More">
              <IconButton onClick={(event) => setMenuAnchor(event.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem onClick={makeDefault} disabled={selected.isDefault}>
                Make default
              </MenuItem>
              <MenuItem onClick={deleteBoard} sx={{ color: 'error.main' }}>
                Delete wallboard
              </MenuItem>
            </Menu>
            {tab === 'layout' && (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={save}
                disabled={!dirty || saving}
                sx={{ textTransform: 'none' }}
              >
                {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
              </Button>
            )}
          </Stack>

          {tab === 'layout' ? (
            <ScreenEditor draft={draft} updateDraft={updateDraft} kpiSlugs={kpiSlugs} />
          ) : (
            <DevicesPanel boards={boards} setToast={setToast} />
          )}
        </>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>New wallboard</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Name"
            placeholder="e.g. Crew room"
            value={newBoardName}
            onChange={(event) => setNewBoardName(event.target.value)}
            inputProps={{ maxLength: 60 }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createBoard}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}

export default WallboardManager
